import type { LayerStackSprite } from '../../layerstack/LayerStackSprite';

/**
 * The single writer of the player ship's state.
 *
 * Two systems want that state: the controller says `cruise` or `firing` every
 * frame from held input, and the FX side wants a timed `damaged` override. Let
 * them both call `setState` and they alternate once per frame — and because the
 * sound binder fires a cue on every state entry, an alternating pair is audible
 * spam, not just wasted work. So neither of them touches the sprite: the
 * controller writes `base`, FX writes the override, and `apply` resolves them
 * once per frame. `setState` is idempotent, so one resolved value per frame
 * means at most one listener event per actual change.
 */
export type PlayerBaseState = 'cruise' | 'firing';

interface StateOverride {
  readonly state: string;
  remainingMs: number;
}

export class PlayerStateArbiter {
  private baseState: PlayerBaseState = 'cruise';
  private override: StateOverride | null = null;
  private lastApplied: string | null = null;
  private transitionCount = 0;

  /** Controller intent for this frame. Cheap enough to call unconditionally. */
  setBase(state: PlayerBaseState): void {
    this.baseState = state;
  }

  /** Retriggering restarts the timer rather than stacking a second override. */
  flash(state: string, ms: number): void {
    if (ms <= 0) return;
    const running = this.override;
    if (running && running.state === state) {
      running.remainingMs = ms;
      return;
    }
    this.override = { state, remainingMs: ms };
  }

  /** Timebase-scaled, so a hit stop holds the override open for its full visible time. */
  update(deltaMs: number): void {
    const running = this.override;
    if (!running) return;
    running.remainingMs -= deltaMs;
    if (running.remainingMs > 0) return;
    this.override = null;
  }

  resolve(): string {
    return this.override?.state ?? this.baseState;
  }

  /**
   * The one write per frame. A state the manifest does not author is skipped —
   * `setState` would swap in a draw list that does not exist.
   */
  apply(sprite: LayerStackSprite): void {
    const next = this.resolve();
    if (!sprite.states.includes(next)) return;
    if (next !== this.lastApplied) this.transitionCount += 1;
    this.lastApplied = next;
    sprite.setState(next);
  }

  get base(): PlayerBaseState {
    return this.baseState;
  }

  get overrideState(): string {
    return this.override?.state ?? '';
  }

  get overrideRemainingMs(): number {
    return this.override?.remainingMs ?? 0;
  }

  /** Read-out for the acceptance check: this must move once per real change, never per frame. */
  get transitions(): number {
    return this.transitionCount;
  }

  /** A scene rebuild hands over a fresh sprite; a surviving override would paint it. */
  reset(): void {
    this.baseState = 'cruise';
    this.override = null;
    this.lastApplied = null;
  }
}
